import { useParams, Link, Navigate } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import Seo, { faqSchema, localBusinessSchema } from "@/components/Seo";
import Reveal from "@/components/Reveal";
import Faq from "@/components/Faq";
import EnrollForm from "@/components/EnrollForm";
import { serviceBySlug, SERVICES } from "@/data/site";

export default function ServiceDetail() {
  const { slug } = useParams();
  const s = serviceBySlug(slug);
  if (!s) return <Navigate to="/" replace />;
  const others = SERVICES.filter((x) => x.slug !== slug).slice(0, 3);

  const schema = {
    "@context": "https://schema.org",
    "@type": "MedicalTherapy",
    name: s.name,
    description: s.meta,
    provider: localBusinessSchema,
  };

  return (
    <>
      <Seo title={s.title} description={s.meta} path={`/services/${s.slug}`} image={s.image} jsonLd={[schema, faqSchema(s.faqs)]} />

      <section className="border-b border-border">
        <div className="mx-auto grid max-w-6xl items-center gap-12 px-6 py-16 md:grid-cols-2 md:py-24">
          <Reveal>
            <div className="overline mb-5">Service · {s.num}</div>
            <h1 className="font-serif text-4xl leading-tight text-ink sm:text-5xl lg:text-6xl">{s.h1}</h1>
            <p className="mt-4 font-serif text-2xl italic text-clay">{s.tagline}</p>
            <p className="mt-6 max-w-md font-sans text-base leading-relaxed text-ink-soft">{s.intro}</p>
            <Link to="/new-patient" data-testid="service-cta" className="mt-8 inline-flex rounded-full bg-ink px-8 py-3.5 font-sans text-sm text-linen transition-colors hover:bg-ink/85">Begin your enrollment</Link>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="aspect-[4/5] overflow-hidden"><img src={s.image} alt={s.name + " in Waterbury, CT"} className="h-full w-full object-cover" loading="eager" /></div>
          </Reveal>
        </div>
      </section>

      <section className="border-b border-border bg-white py-24">
        <div className="mx-auto grid max-w-5xl gap-14 px-6 md:grid-cols-5">
          <div className="prose-editorial md:col-span-3">
            <h2 className="font-serif text-3xl text-ink">How we help</h2>
            {s.body.map((p, i) => <p key={i}>{p}</p>)}
            <h2 className="font-serif text-3xl text-ink">What to expect</h2>
            <p>{s.expect}</p>
          </div>
          <aside className="md:col-span-2">
            <div className="border border-border p-8">
              <div className="overline mb-5">Our modalities</div>
              <ul className="space-y-3">
                {s.approaches.map((a) => (
                  <li key={a} className="flex items-start gap-3 font-sans text-sm text-ink-soft"><Check size={16} className="mt-0.5 shrink-0 text-clay" />{a}</li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>

      <Faq faqs={s.faqs} label={`${s.short} questions`} />

      <section className="border-t border-border py-20">
        <div className="mx-auto max-w-6xl px-6">
          <div className="overline mb-8">Explore more care</div>
          <div className="grid gap-px overflow-hidden border border-border bg-border sm:grid-cols-3">
            {others.map((o) => (
              <Link key={o.slug} to={`/services/${o.slug}`} className="group bg-white p-8 transition-colors hover:bg-linen">
                <h3 className="font-serif text-xl text-ink">{o.short}</h3>
                <span className="mt-4 inline-flex items-center gap-2 font-sans text-xs text-clay group-hover:translate-x-1">Learn more <ArrowRight size={13} /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-border bg-white py-24">
        <div className="mx-auto max-w-3xl px-6">
          <div className="overline mb-4 text-center">Ready when you are</div>
          <h2 className="mb-10 text-center font-serif text-4xl text-ink sm:text-5xl">Start with a <span className="italic text-clay">gentle</span> first step.</h2>
          <EnrollForm />
        </div>
      </section>
    </>
  );
}
